// Monthly aggregation for extra values ("valores não lançados").
// Savings are stored as negative values. New server entries carry the exact,
// signed cent split; legacy entries fall back to value/installments.

// Contrato compartilhado com o agregador BE e limite legado de geração de competências.
const MAX_MONTHS = 600;
const OUT_OF_RANGE_CODE = "monthly_extra_values_out_of_range";
const INVALID_INSTALLMENTS_CODE = "monthly_extra_values_invalid_installments";
const OUT_OF_RANGE_MESSAGE = "Não foi possível calcular os lançamentos por mês com segurança.";
const KNOWN_ERROR_CODES = new Set([OUT_OF_RANGE_CODE, INVALID_INSTALLMENTS_CODE]);

const emptyAggregation = (error) => ({
  months: [],
  monthLabels: [],
  expenses: [],
  savings: [],
  net: [],
  rows: [],
  entriesByMonth: {},
  totals: { expenses: 0, savings: 0, net: 0 },
  ...(error ? { error } : {}),
});

const failOutOfRange = () => {
  const error = new RangeError(OUT_OF_RANGE_CODE);
  error.code = OUT_OF_RANGE_CODE;
  throw error;
};

const failInvalidInstallments = () => {
  const error = new TypeError(INVALID_INSTALLMENTS_CODE);
  error.code = INVALID_INSTALLMENTS_CODE;
  throw error;
};

const addExactCents = (left, right) => {
  const total = left + right;
  if (!Number.isSafeInteger(left) || !Number.isSafeInteger(right) || !Number.isSafeInteger(total)) {
    failOutOfRange();
  }
  return total;
};

const centsToLegacyValue = (cents) => {
  if (!Number.isSafeInteger(cents)) failOutOfRange();
  const value = cents / 100;
  if (!Number.isFinite(value) || Math.round(value * 100) !== cents) failOutOfRange();
  return value;
};

const legacyValueToCents = (value) => {
  const numericValue = Number(value) || 0;
  const cents = Math.round(numericValue * 100);
  if (!Number.isFinite(numericValue) || !Number.isSafeInteger(cents)) failOutOfRange();
  return cents;
};

const sumLegacyValuesExact = (values) => centsToLegacyValue(
  values.reduce((total, value) => addExactCents(total, legacyValueToCents(value)), 0),
);

const remainderAdjustment = (remainder) => {
  if (remainder === 0) return 0;
  return remainder > 0 ? 1 : -1;
};

const compareAbsoluteCents = (left, right) => {
  if (Math.abs(right.valueCents) === Math.abs(left.valueCents)) return 0;
  return Math.abs(right.valueCents) > Math.abs(left.valueCents) ? 1 : -1;
};

const toMonthLabel = (yearMonth) => {
  const [year, month] = yearMonth.split("-");
  return `${month}/${year}`;
};

const nextMonth = (yearMonth) => {
  const [year, month] = yearMonth.split("-").map(Number);
  const totalMonths = year * 12 + (month - 1) + 1;
  return `${Math.floor(totalMonths / 12)}-${String((totalMonths % 12) + 1).padStart(2, "0")}`;
};

const addMonths = (yearMonth, offset) => {
  const [year, month] = yearMonth.split("-").map(Number);
  const totalMonths = (year * 12) + (month - 1) + offset;
  const targetYear = Math.floor(totalMonths / 12);
  if (
    !Number.isSafeInteger(totalMonths)
    || !Number.isInteger(offset)
    || offset < 0
    || targetYear < 0
    || targetYear > 9999
  ) {
    failInvalidInstallments();
  }
  return `${String(targetYear).padStart(4, "0")}-${String((totalMonths % 12) + 1).padStart(2, "0")}`;
};

const monthRange = (start, end) => {
  const months = [];
  let current = start;
  while (current <= end && months.length < MAX_MONTHS) {
    months.push(current);
    current = nextMonth(current);
  }
  return months;
};

const validMonth = (value) => typeof value === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);

const monthInRange = (month, begin, end) => (
  (!begin || month >= begin) && (!end || month <= end)
);

const validatedInstallments = (entry) => {
  const installments = Number(entry.installments);
  if (!Number.isInteger(installments) || installments < 1 || installments > MAX_MONTHS) {
    failInvalidInstallments();
  }
  return installments;
};

const hasInstallmentCents = (entry) => Object.prototype.hasOwnProperty.call(entry, "installmentCents");

const exactInstallments = (entry, installments) => {
  if (!hasInstallmentCents(entry)) return null;
  if (
    !Array.isArray(entry.installmentCents)
    || entry.installmentCents.length === 0
    || entry.installmentCents.length > MAX_MONTHS
    || entry.installmentCents.length !== installments
    || !entry.installmentCents.every(Number.isSafeInteger)
  ) {
    failInvalidInstallments();
  }
  return entry.installmentCents;
};

export const aggregateExtraValuesByMonth = (extraValues, begin, end) => {
  try {
    const entries = [];
    let beginMonth = validMonth(begin) ? begin : null;
    let endMonth = validMonth(end) ? end : null;
    if (beginMonth && endMonth && beginMonth > endMonth) {
      [beginMonth, endMonth] = [endMonth, beginMonth];
    }

    (Array.isArray(extraValues) ? extraValues : []).forEach((resource) => {
      (resource.history || []).forEach((historyEntry) => {
        if (!historyEntry.isValid || !validMonth(historyEntry.start_date)) return;
        if (endMonth && historyEntry.start_date > endMonth) return;
        const installments = validatedInstallments(historyEntry);
        const lastMonth = addMonths(historyEntry.start_date, installments - 1);
        // Um registro anterior só é descartado depois que seu bound final foi
        // validado e derivado em O(1). Sem bounds confiáveis, falha fechado.
        if (beginMonth && lastMonth < beginMonth) return;
        const installmentCents = exactInstallments(historyEntry, installments);
        const range = installmentCents
          ? Array.from(
            { length: installmentCents.length },
            (_, index) => addMonths(historyEntry.start_date, index),
          )
          : monthRange(historyEntry.start_date, lastMonth);
        const visibleMonths = new Set(range.filter((month) => monthInRange(month, beginMonth, endMonth)));
        if (visibleMonths.size === 0) return;
        const legacyTotalCents = installmentCents ? null : legacyValueToCents(historyEntry.value);
        const legacyInstallmentCents = installmentCents ? null : Math.trunc(legacyTotalCents / installments);
        let remainder = installmentCents
          ? 0
          : legacyTotalCents - (legacyInstallmentCents * installments);
        range.forEach((month, installmentIndex) => {
          const adjustment = remainderAdjustment(remainder);
          if (!installmentCents) remainder -= adjustment;
          if (!visibleMonths.has(month)) return;
          const valueCents = installmentCents?.[installmentIndex]
            ?? addExactCents(legacyInstallmentCents, adjustment);
          entries.push({
            wbs_code_id: resource.wbs_code_id,
            item_name: resource.item_name,
            description: historyEntry.description || "",
            month,
            valueCents,
            type: valueCents < 0 ? "economia" : "despesa",
            installment_index: installmentIndex + 1,
            installments,
            start_date: historyEntry.start_date,
            end_date: historyEntry.end_date || historyEntry.start_date,
          });
        });
      });
    });

    if (entries.length === 0) return emptyAggregation();

    const sortedMonths = entries.map((entry) => entry.month).sort();
    const months = monthRange(sortedMonths[0], sortedMonths[sortedMonths.length - 1]);
    const expensesCents = Object.fromEntries(months.map((month) => [month, 0]));
    const savingsCents = Object.fromEntries(months.map((month) => [month, 0]));
    const rowsByItem = new Map();
    const entriesByMonth = {};

    entries.forEach((entry) => {
      const target = entry.valueCents < 0 ? savingsCents : expensesCents;
      target[entry.month] = addExactCents(target[entry.month] ?? 0, entry.valueCents);

      if (!rowsByItem.has(entry.wbs_code_id)) {
        rowsByItem.set(entry.wbs_code_id, {
          wbs_code_id: entry.wbs_code_id,
          item_name: entry.item_name,
          values: {},
          total: 0,
        });
      }
      const row = rowsByItem.get(entry.wbs_code_id);
      row.values[entry.month] = addExactCents(row.values[entry.month] ?? 0, entry.valueCents);
      row.total = addExactCents(row.total, entry.valueCents);

      (entriesByMonth[entry.month] ||= []).push(entry);
    });

    const expenses = months.map((month) => centsToLegacyValue(expensesCents[month]));
    const savings = months.map((month) => centsToLegacyValue(savingsCents[month]));
    const net = months.map((month) => centsToLegacyValue(
      addExactCents(expensesCents[month], savingsCents[month]),
    ));
    const safeEntriesByMonth = Object.fromEntries(
      Object.entries(entriesByMonth).map(([month, monthEntries]) => [month, monthEntries
        .sort(compareAbsoluteCents)
        .map(({ valueCents, ...entry }) => ({ ...entry, value: centsToLegacyValue(valueCents) }))]),
    );
    const rows = Array.from(rowsByItem.values()).map((row) => ({
      ...row,
      values: Object.fromEntries(
        Object.entries(row.values).map(([month, cents]) => [month, centsToLegacyValue(cents)]),
      ),
      total: centsToLegacyValue(row.total),
    })).sort((a, b) => String(a.item_name ?? "").localeCompare(String(b.item_name ?? "")));

    return {
      months,
      monthLabels: months.map(toMonthLabel),
      expenses,
      savings,
      net,
      entriesByMonth: safeEntriesByMonth,
      rows,
      totals: {
        expenses: sumLegacyValuesExact(expenses),
        savings: sumLegacyValuesExact(savings),
        net: sumLegacyValuesExact(net),
      },
    };
  } catch (error) {
    if (!KNOWN_ERROR_CODES.has(error?.code)) throw error;
    return emptyAggregation({ code: error.code, message: OUT_OF_RANGE_MESSAGE });
  }
};

export const extraValueMonths = (extraValues = []) => {
  try {
    const boundaries = [];
    let errorCode = null;
    (Array.isArray(extraValues) ? extraValues : []).forEach((resource) => {
      (resource.history || []).forEach((entry) => {
        if (entry?.isValid !== true || !validMonth(entry.start_date)) return;
        boundaries.push(entry.start_date);
        try {
          const installments = validatedInstallments(entry);
          boundaries.push(addMonths(entry.start_date, installments - 1));
        } catch (error) {
          if (!KNOWN_ERROR_CODES.has(error?.code)) throw error;
          errorCode ??= error.code;
        }
      });
    });
    const sorted = boundaries.filter(validMonth).sort();
    const months = sorted.length === 0 ? [] : monthRange(sorted[0], sorted[sorted.length - 1]);
    return {
      months,
      ...(errorCode ? { error: { code: errorCode } } : {}),
    };
  } catch (error) {
    if (!KNOWN_ERROR_CODES.has(error?.code)) throw error;
    return { months: [], error: { code: error.code } };
  }
};

export { toMonthLabel };

// Recorta o resultado da agregação para uma faixa de meses [from, to] (YYYY-MM).
// Mantém `allMonths`/`allMonthLabels` (faixa cheia, p/ popular o filtro) e
// recalcula os campos de exibição só para os meses visíveis. Itens sem nenhum
// lançamento na faixa somem da tabela.
export const sliceMonthlyByRange = (full, from, to) => {
  const allMonths = full.months;
  const allMonthLabels = full.monthLabels;
  const base = { ...full, allMonths, allMonthLabels };
  if (allMonths.length === 0) return base;

  let f = from || allMonths[0];
  let t = to || allMonths[allMonths.length - 1];
  if (f > t) [f, t] = [t, f];

  const visibleIdx = allMonths.map((m, i) => [m, i]).filter(([m]) => m >= f && m <= t);
  const months = visibleIdx.map(([m]) => m);
  const monthLabels = months.map(toMonthLabel);
  const expenses = visibleIdx.map(([, i]) => full.expenses[i]);
  const savings = visibleIdx.map(([, i]) => full.savings[i]);
  const net = visibleIdx.map(([, i]) => full.net[i]);

  const rows = full.rows
    .map((row) => ({ ...row, total: sumLegacyValuesExact(months.map((m) => row.values[m] || 0)) }))
    .filter((row) => months.some((m) => row.values[m]));

  return {
    ...base,
    months,
    monthLabels,
    expenses,
    savings,
    net,
    rows,
    totals: {
      expenses: sumLegacyValuesExact(expenses),
      savings: sumLegacyValuesExact(savings),
      net: sumLegacyValuesExact(net),
    },
  };
};
